import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Button } from '@accio3d/design-system/button';
import { Popover, PopoverContent, PopoverTrigger } from '@accio3d/design-system/popover';
import { cn } from '@accio3d/design-system/utils';

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from '../../hooks/useNotifications';

function timeAgo(value: string): string {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function NotificationsBell({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { data: notifications = [] } = useNotifications({ limit: 20 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const badge = unreadCount > 0 ? (unreadCount > 99 ? '99+' : String(unreadCount)) : null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={badge ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          className={cn(
            'relative flex items-center rounded-sm transition-colors text-white/80 hover:bg-white/10 hover:text-white',
            collapsed
              ? 'size-[34px] shrink-0 justify-center'
              : 'w-full justify-start gap-3 px-4 py-2.5 text-left',
          )}
        >
          <span className="relative flex shrink-0 items-center">
            <Bell className="h-5 w-5 shrink-0" />
            {badge && (
              <span
                aria-hidden
                className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold leading-none text-slate-900"
              >
                {badge}
              </span>
            )}
          </span>
          {!collapsed && <span className="text-sm font-medium">Notifications</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <p className="text-sm font-medium text-foreground">Notifications</p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No notifications yet.
            </p>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                className={cn(
                  'block w-full border-b border-border px-3 py-2.5 text-left last:border-0 hover:bg-muted/40',
                  !notification.readAt && 'bg-primary/5',
                )}
                onClick={() => {
                  if (!notification.readAt) markRead.mutate(notification.id);
                  navigate(notification.link);
                }}
              >
                <p className="text-sm font-medium text-foreground">{notification.title}</p>
                {notification.body && (
                  <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                    {notification.body}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {timeAgo(notification.createdAt)}
                </p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
